import { Component, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import {
  CucmLicenseOptimizationContext,
  LicenseOptimizationReport,
} from '../../contexts/cucm-license-optimization.context';
import { combineLatest, Subscription } from 'rxjs';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { UiOptions, UnusedDevicesReport } from '../../../../shared/models/generated/ziroModels';
import { ToastService } from '../../../../shared/services/toast.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { CucmCdrUnusedDevicesContext } from '../../contexts/cucm-cdr-unused-devices.context';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomUiOptionsContext } from '../../../../shared/contexts/custom-ui-options.context';
import { get } from 'lodash';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface LicenseOptimizationRow extends DatatableRow {
  type: string;
  currentTotal: number;
  optimalTotal: number;
  optimizableDevices: number;
}
@Component({
  selector: 'ziro-cucm-license-optimization-overview',
  templateUrl: './cucm-license-optimization-overview.component.html',
  styleUrls: ['../../../reporting.scss', './cucm-license-optimization-overview.component.scss'],
  standalone: false,
})
export class CucmLicenseOptimizationOverviewComponent implements OnInit, OnDestroy {
  private bottomNavService = inject(BottomNavService);
  private breadcrumbsService = inject(BreadcrumbsService);
  private cucmCdrUnusedDevicesContext = inject(CucmCdrUnusedDevicesContext);
  private licenseOptimizationContext = inject(CucmLicenseOptimizationContext);
  private toastService = inject(ToastService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _customUiOptionsContext = inject(CustomUiOptionsContext);

  ziroIcons = ZiroIcons;

  licenseTier = decodeURIComponent(get(this._route, 'snapshot.params.licenseTier'));
  licenseTiers = ['CUWL Standard', 'Enhanced Plus', 'Enhanced', 'Basic', 'Essential'];
  licenseOptimizationReport: LicenseOptimizationReport;
  unusedDevicesReport: UnusedDevicesReport;
  isLoading = true;
  columns: DatatableColumn<LicenseOptimizationRow>[] = [
    {
      name: 'type',
      columnSize: 'col-sm-3',
      label: 'tkey;reports.cucm_license_optimizer.overview.license_type',
    },
    {
      name: 'currentTotal',
      columnSize: 'col-sm-2',
      label: 'tkey;reports.cucm_license_optimizer.overview.current_licenses',
    },
    {
      name: 'optimalTotal',
      columnSize: 'col-sm-2',
      label: 'tkey;reports.cucm_license_optimizer.overview.optimal_licenses',
    },
    {
      name: 'optimizableDevices',
      columnSize: 'col-sm-4',
      label: 'tkey;reports.cucm_license_optimizer.overview.optimizable_devices',
    },
  ];
  tableRows: LicenseOptimizationRow[] = [];

  bottomNavButtons = [
    {
      id: 'exportLicenseOptimizerReport',
      dataAutomation: 'export-license-optimizer-report-button',
      label: 'tkey;reports.xlsx_export.button',
      icon: ZiroIcons.EXPORT,
      buttonClass: ButtonStyles.PRIMARY,
      cb: () => {
        this.onGetFileClicked();
      },
    },
  ];

  removeEventListener: () => void;

  private _reportingEnabled = false;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.cucm_license_optimizer.overview.title' }]);

    const subscription = combineLatest([
      this.cucmCdrUnusedDevicesContext.state$,
      this.licenseOptimizationContext.report$,
    ]).subscribe(([unusedDevicesReport, report]: [UnusedDevicesReport, LicenseOptimizationReport]) => {
      this.unusedDevicesReport = unusedDevicesReport;
      this._updateExportDisabledStatus();
      this.licenseOptimizationReport = report;
      this._updateExportDisabledStatus();
      this._buildTable();
      this.isLoading = false;
    });

    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));

    const uiOptionsSub = this._customUiOptionsContext.state$.subscribe((uiOptions: UiOptions) => {
      this._reportingEnabled = uiOptions.reportingEnabled;
      this._updateExportDisabledStatus();
    });

    this._subscriptions.add(subscription);
    this._subscriptions.add(uiOptionsSub);

    this.removeEventListener = this.renderer.listen(this.elementRef.nativeElement, 'click', (event) => {
      if (
        event.target instanceof HTMLAnchorElement &&
        event.target.getAttribute('class') &&
        event.target.getAttribute('class').includes('excel-export')
      ) {
        this.onAnchorClicked(event);
      }
    });
  }

  ngOnDestroy(): void {
    this.removeEventListener();
    this._subscriptions.unsubscribe();
  }

  private onAnchorClicked(event: Event): void {
    event.preventDefault();
    this.onGetFileClicked();
  }

  onGetFileClicked(): void {
    if (this._reportingEnabled) {
      this._setExportPending(true);
      this.licenseOptimizationContext.downloadFile().subscribe(() => {
        this._setExportPending(false);
        this.toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.EXPORT,
          'tkey;reports.xlsx_export.downloaded_toast.title',
          'tkey;reports.xlsx_export.downloaded_toast.message',
        );
      });
    }
  }

  private _setExportPending(setting: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'exportLicenseOptimizerReport',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
          tooltipVisible: true,
        },
      }),
    );
  }

  private _updateExportDisabledStatus(): void {
    const isDisabled = this.isLoading || this.unusedDevicesReport?.numCalls === 0 || !this._reportingEnabled;
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'exportLicenseOptimizerReport',
        state: {
          pending: false,
          buttonDisableState: {
            disabled: isDisabled,
            tooltipKey: 'tkey;reports.cucm_license_optimizer.export.button.disabled.tooltip',
          },
          tooltipVisible: isDisabled,
        },
      }),
    );
  }

  private _buildTable(): void {
    this.tableRows = this.licenseTiers.map((type) => {
      return {
        type: type,
        currentTotal: this.licenseOptimizationReport[type].currentTotal,
        optimalTotal: this.licenseOptimizationReport[type].optimalTotal,
        optimizableDevices: this.licenseOptimizationReport[type].devices.length,
        cssClass: this.licenseOptimizationReport[type].devices.length > 0 ? 'table-warning' : '',
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'candidates-button',
            icon: ZiroIcons.NEXT,
            disabled: this.licenseOptimizationReport[type].devices.length === 0,
            disabledTooltip: 'tkey;reports.cucm_license_optimizer.overview.candidates_button.tooltip',
            onClick: () => this._nextClicked(type),
          },
        ],
      };
    });
  }

  private _nextClicked(type: string): void {
    const url = encodeURIComponent(type);
    this._router.navigate([url], { relativeTo: this._route });
  }
}
